// ── Category Registry ──────────────────────────────────────────────────────────
// Higher-level groupings shown as chips on the landing page.
// Categories deliberately span multiple domains so composite/hybrid use-cases
// (e.g. "body armor") naturally pull in both crystal and polymer models.
//
// Used to:
//   1. Render landing-page category chips so users can orient themselves.
//   2. Pre-set resolved_domains before the Stage 2 LLM decomposer runs, keeping
//      the decomposer prompt scoped to only the relevant model sections.
//
// Adding a new model? Add or update entries here if the model introduces a domain
// that isn't already covered by an existing category.

export const CATEGORY_REGISTRY = [
  {
    id: 'structural_materials',
    label: 'Structural Materials',
    icon: '⚙',
    description: 'High-strength alloys, ceramics, and refractory materials for load-bearing applications',
    domains: ['inorganic_crystal'],
    example: 'High-strength alloy for aerospace turbine blades with low density',
  },
  {
    id: 'energy_materials',
    label: 'Energy & Storage',
    icon: '⚡',
    description: 'Battery electrodes, solid-state electrolytes, photovoltaics, thermoelectrics',
    domains: ['inorganic_crystal'],
    example: 'Solid-state electrolyte for next-generation lithium-ion batteries',
  },
  {
    id: 'functional_electronic',
    label: 'Electronic & Magnetic',
    icon: '🔬',
    description: 'Semiconductors, superconductors, permanent magnets, optical materials',
    domains: ['inorganic_crystal'],
    example: 'Wide-band-gap semiconductor for high-power switching devices',
  },
  {
    id: 'drug_discovery',
    label: 'Drug Discovery',
    icon: '💊',
    description: 'Drug-like small molecules with ADMET properties, binding affinity, and bioavailability',
    domains: ['small_molecule'],
    example: 'Orally bioavailable molecule targeting a kinase with high QED',
  },
  {
    id: 'polymer_fiber',
    label: 'Polymer & Fiber',
    icon: '🧵',
    description: 'Structural polymers, high-tenacity fibers, resins, and elastomers',
    domains: ['polymer'],
    example: 'Ultra-high-molecular-weight polymer fiber for flexible protective gear',
  },
  {
    id: 'composite_hybrid',
    label: 'Composite & Hybrid',
    icon: '🔩',
    description: 'Multi-material systems combining inorganic and polymer components for extreme performance',
    domains: ['inorganic_crystal', 'polymer'],
    example: 'Ceramic-polymer composite laminate for ballistic armor inserts',
  },
  {
    id: 'bioactive_molecular',
    label: 'Bioactive Molecules',
    icon: '⚗️',
    description: 'Ligands, peptide mimetics, and functional small molecules for biological applications',
    domains: ['small_molecule'],
    example: 'Protease inhibitor with low synthetic accessibility score',
  },
  {
    id: 'catalysis',
    label: 'Catalysis',
    icon: '🌡',
    description: 'Heterogeneous catalysts, porous oxides, and metal-organic frameworks',
    domains: ['inorganic_crystal'],
    example: 'Transition-metal oxide catalyst for CO2 reduction with high surface area',
  },
];

/**
 * Filter categories by an allowlist. If allowedIds is null/empty, returns all.
 */
export function getFilteredCategories(allowedIds) {
  if (!allowedIds || allowedIds.length === 0) return CATEGORY_REGISTRY;
  const set = new Set(allowedIds);
  return CATEGORY_REGISTRY.filter(c => set.has(c.id));
}

/**
 * Return all unique domains that appear in at least one category.
 */
export function getAllCategoryDomains() {
  const seen = new Set();
  for (const c of CATEGORY_REGISTRY) {
    for (const d of c.domains) seen.add(d);
  }
  return Array.from(seen);
}

/**
 * Build a compact description string for the Stage 1 LLM prompt.
 * Each line: "id: label — description → domains: [domain1, domain2]"
 * Pass allowedIds to scope the prompt to only licensed categories.
 */
export function buildCategoryPromptLines(allowedIds) {
  const cats = getFilteredCategories(allowedIds);
  return cats.map(c =>
    `- ${c.id}: ${c.label} — ${c.description} → domains: [${c.domains.join(', ')}]`
  ).join('\n');
}
